import { useRef } from 'react';
import styles from './LevelRoadmap.module.css';
import shared from '../HomePage.module.css';
import useLevelStoryPreview from '../hooks/useLevelStoryPreview.js';

const LEVELS = [
  {
    level: 'LV.1',
    title: 'POP',
    shortCopy: '영어의 즐거움을 처음 터뜨려요',
    copy: '영어의 즐거움을 처음 발견하는 단계입니다. 밝고 친근한 스토리로 아이가 영어를 놀이처럼 받아들이도록 시작합니다.',
    image: '/story/Lv.POP1.png',
  },
  {
    level: 'LV.2',
    title: 'TAP',
    shortCopy: '소리와 파닉스를 두드려요',
    copy: '소리와 파닉스를 두드리듯 익히는 단계입니다. 리듬감 있는 장면으로 알파벳과 소리의 연결을 자연스럽게 만납니다.',
    image: '/story/Lv.TAP2.png',
  },
  {
    level: 'LV.3',
    title: 'RUN',
    shortCopy: '짧은 문장으로 말하기 시작해요',
    copy: '짧은 문장으로 말하기를 시작하는 단계입니다. 익숙한 표현을 빠르게 반복하며 영어 말문이 열리도록 돕습니다.',
    image: '/story/Lv.RUN3.png',
  },
  {
    level: 'LV.4',
    title: 'BUILD',
    shortCopy: '문장 구조를 차곡차곡 쌓아요',
    copy: '문장 구조를 차곡차곡 쌓는 단계입니다. 단어와 표현을 조합해 아이가 스스로 문장을 만들 수 있게 합니다.',
    image: '/story/Lv.BUILD4.png',
  },
  {
    level: 'LV.5',
    title: 'JUMP',
    shortCopy: '자신감 있게 대화로 도약해요',
    copy: '자신감 있게 대화로 도약하는 단계입니다. 상황 속 표현을 익히며 말하기 반응 속도와 자신감을 키웁니다.',
    image: '/story/Lv.JUMP5.png',
  },
  {
    level: 'LV.6',
    title: 'LINK',
    shortCopy: '읽기·쓰기·말하기를 연결해요',
    copy: '읽기, 쓰기, 말하기를 연결하는 단계입니다. 배운 표현을 여러 활동으로 확장해 종합적인 영어 감각을 만듭니다.',
    image: '/story/Lv.LINK6.png',
  },
];

/**
 * ═══ LEVEL ROADMAP ═══
 * 상단에 "특별한 파닉스 과정" 인트로(호버 시 뒷면 캐릭터 카드 2장, 순수 CSS :hover)와
 * POP~LINK 6단계 로드맵을 담는다. 하단 레벨 트랙(칩)과 오른쪽 스토리 미리보기 카드는
 * hooks/useLevelStoryPreview.js가 rootRef 안의 [data-level-*] 요소들을 찾아 자동 회전시킨다.
 */
export default function LevelRoadmap() {
  const rootRef = useRef(null);
  useLevelStoryPreview(rootRef);

  const first = LEVELS[0];

  return (
    <section className={`${shared.section} ${styles.levels}`} id="levels">
      <div className="wrap" ref={rootRef}>
        {/* 파닉스 과정 인트로 (2단계: Phonics Friends / Phonics Heroes. 호버 시 뒷면 캐릭터 등장) */}
        <div className={styles.phonicsIntro}>
          <div className={`${shared.secHead} ${styles.phonicsSecHead} ${shared.reveal}`}>
            <span className={shared.eyebrow}>Level System</span>
            <h2>
              ENGLISH POP만의
              <br />
              <span className={shared.hl}>특별한 파닉스 과정</span>
            </h2>
            <p>알파벳 인지부터 자연스러운 문장 읽기까지, 영어의 첫 걸음을 단단하게 완성하는 학습자 맞춤형 파닉스 프로그램입니다.</p>
          </div>
          <div className={`${styles.phonicsGrid} ${shared.reveal}`}>
            <div className={styles.phonicsCard}>
              <div className={`${styles.pcFace} ${styles.pcFront}`}>
                <h3 className="en">Phonics Friends</h3>
                <p>알파벳을 처음 만나요!</p>
              </div>
              <div className={`${styles.pcFace} ${styles.pcBack}`}>
                <p className={styles.pcText}>
                  알파벳 대·소문자의 형태 인식부터 첫소리 학습까지, 파닉스의 기초를 탄탄하게 다지는
                  <span className={styles.pcEmph}>입문 파닉스 과정입니다.</span>
                </p>
                <img src="/assets/phonics_croc.png" alt="" />
              </div>
            </div>
            <div className={styles.phonicsCard}>
              <div className={`${styles.pcFace} ${styles.pcFront}`}>
                <h3 className="en">Phonics Heroes</h3>
                <p>소리를 배우며 읽기를 시작해요!</p>
              </div>
              <div className={`${styles.pcFace} ${styles.pcBack}`}>
                <p className={styles.pcText}>
                  체계적인 음소 합성 훈련을 통해 파닉스 규칙을 완벽하게 이해하여 단어와 문장을 읽는 힘을 길러주는
                  <span className={styles.pcEmph}>심화 파닉스 과정입니다.</span>
                </p>
                <img src="/assets/phonics_otter.png" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div className={`${shared.secHead} ${shared.reveal}`}>
          <h2>
            POP에서 LINK까지,
            <br />
            <span className={shared.hl}>6단계</span> 성장 로드맵
          </h2>
          <p>말문을 터뜨리고(POP) 읽기·쓰기·말하기를 하나로 잇기까지(LINK) 단계마다 명확한 목표가 있습니다.</p>
        </div>

        <div className={`${styles.levelTrack} ${shared.reveal}`} data-level-track>
          {LEVELS.map((lvl, i) => (
            <div
              key={lvl.level}
              className={`${styles.levelStep}${i === 0 ? ' is-active' : ''}`}
              role="button"
              tabIndex={0}
              data-level-step="true"
              data-level={lvl.level}
              data-title={lvl.title}
              data-copy={lvl.copy}
              data-image={lvl.image}
            >
              <div className={`${styles.levelDot} en`}>{lvl.title}</div>
              <p>{lvl.shortCopy}</p>
            </div>
          ))}
        </div>

        <div className={`${styles.levelStory} ${shared.reveal}`} aria-live="off">
          <div className={styles.levelStoryCopy}>
            <p className={styles.levelStoryKicker} data-level-kicker>
              {first.level} {first.title}
            </p>
            <h3 className={styles.levelStoryTitle} data-level-title>
              {first.title} 스토리
            </h3>
            <p className={styles.levelStoryText} data-level-text>
              {first.copy}
            </p>
          </div>
          <div className={styles.levelStoryMedia}>
            <img data-level-image src={first.image} alt={`${first.level} ${first.title} 스토리 이미지`} decoding="async" />
          </div>
        </div>
      </div>
    </section>
  );
}
